import React, { createContext, useState, useContext } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';

const TransferContext = createContext(null);

export const useTransfer = () => {
  const context = useContext(TransferContext);
  if (!context) {
    throw new Error('useTransfer must be used within TransferProvider');
  }
  return context;
};

export const TransferProvider = ({ children }) => {
  const [transfers, setTransfers] = useState([]);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchTransfers = async (projectId) => {
    try {
      setLoading(true);
      const response = await api.get(`/transfer/project/${projectId}/`);
      const data = response.data.results || response.data || [];
      setTransfers(Array.isArray(data) ? data : []);
      return data;
    } catch (error) {
      console.error('Error fetching transfers:', error);
      toast.error('Failed to fetch calibration transfers');
      setTransfers([]);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const fetchTransferDetail = async (transferId) => {
    try {
      setLoading(true);
      const response = await api.get(`/transfer/${transferId}/`);
      setSelectedTransfer(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching transfer detail:', error);
      toast.error('Failed to fetch transfer details');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const trainTransfer = async (projectId, sourceSensorId, targetSensorId, method, pairedSamples = null) => {
    try {
      setLoading(true);
      const payload = {
        source_sensor_id: sourceSensorId,
        target_sensor_id: targetSensorId,
        method,
      };
      if (pairedSamples) {
        payload.paired_samples = pairedSamples;
      }
      const response = await api.post(`/transfer/project/${projectId}/train/`, payload);
      const scores = response.data.scores || {};
      toast.success(
        `Transfer model trained! R\u00B2=${(scores.r2 || 0).toFixed(4)}, RMSE=${(scores.rmse || 0).toFixed(4)}`
      );
      return response.data;
    } catch (error) {
      const msg = error.response?.data?.detail || error.response?.data?.[0] || 'Failed to train transfer model';
      toast.error(msg);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const applyTransfer = async (transferId, sampleIds, saveAsNew = false) => {
    try {
      setLoading(true);
      const response = await api.post(`/transfer/${transferId}/apply/`, {
        sample_ids: sampleIds,
        save_as_new: saveAsNew,
      });
      const count = response.data.count || 0;
      if (saveAsNew) {
        toast.success(`${count} spectra transferred and saved as new samples`);
      } else {
        toast.success(`${count} spectra transferred (preview)`);
      }
      return response.data;
    } catch (error) {
      toast.error('Failed to apply transfer');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const deleteTransfer = async (transferId) => {
    try {
      await api.delete(`/transfer/${transferId}/`);
      toast.success('Transfer model deleted');
    } catch (error) {
      toast.error('Failed to delete transfer model');
      throw error;
    }
  };

  const toggleTransferActive = async (transferId, isActive = null) => {
    try {
      setLoading(true);
      const response = await api.post(`/transfer/${transferId}/toggle-active/`, {
        is_active: isActive,
      });
      const status = response.data.is_active ? 'activated' : 'deactivated';
      toast.success(`Transfer model ${status}`);
      return response.data;
    } catch (error) {
      toast.error('Failed to toggle transfer status');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    transfers,
    setTransfers,
    selectedTransfer,
    setSelectedTransfer,
    loading,
    fetchTransfers,
    fetchTransferDetail,
    trainTransfer,
    applyTransfer,
    deleteTransfer,
    toggleTransferActive,
  };

  return <TransferContext.Provider value={value}>{children}</TransferContext.Provider>;
};
